import type { StateThresholdReviewDraft } from '../types/state-thresholds.ts';
import type { StateThresholdDefinition } from '../../tri-shared/types/db.ts';

export const STATE_ASSESSMENT_MIN_LENGTH = 1;

export const isAnyQuantityStateRule = (definition: StateThresholdDefinition) =>
  definition.quantities.length > 0 && definition.quantities.every((q) => q.kind === 'presence');

/** A review records an assessment of all inventory in scope, never just a dismissed warning. */
export function stateThresholdReviewIssues(
  definition: StateThresholdDefinition,
  input: StateThresholdReviewDraft,
  entryKeys: ReadonlySet<string>,
): string[] {
  const issues: string[] = [];
  if (input.outcome === null || !['not_applicable', 'below_threshold', 'reporting_required'].includes(input.outcome))
    issues.push('Choose an assessment outcome.');
  if (input.assessment.trim().length < STATE_ASSESSMENT_MIN_LENGTH)
    issues.push(
      input.outcome === 'not_applicable'
        ? `Explain why this rule does not apply (at least ${STATE_ASSESSMENT_MIN_LENGTH} character).`
        : `Explain the inventory assessed and applicable quantities, units, classifications or exceptions (at least ${STATE_ASSESSMENT_MIN_LENGTH} character).`,
    );
  if (!input.citation.trim()) issues.push('Record the source or supporting assessment reference.');
  if (input.outcome === 'below_threshold' && isAnyQuantityStateRule(definition))
    issues.push(
      'This rule applies at any quantity. If no applicable material is present, record why the rule does not apply.',
    );
  if (input.outcome === 'reporting_required') {
    if (definition.program === 'tier_ii') {
      if (!input.entryKeys.length)
        issues.push('Select the chemicals or products that cover the state reporting requirement.');
      if (input.entryKeys.some((key) => !entryKeys.has(key)))
        issues.push(
          'A selected report entry is no longer available or has no remaining chemical contributions. Review the selected chemicals and products.',
        );
    } else if (input.filingPlan.trim().length < STATE_ASSESSMENT_MIN_LENGTH)
      issues.push(
        `Explain how the separate state-program filing will be handled (at least ${STATE_ASSESSMENT_MIN_LENGTH} character). This Tier II export does not establish that filing.`,
      );
  }
  return issues;
}

const isNullableString = (value: unknown) => value === null || typeof value === 'string';

/** Refuse executable-looking unknown JSON rather than trusting a cast from persistence. */
export function isStateThresholdDefinition(value: unknown): value is StateThresholdDefinition {
  if (!value || typeof value !== 'object') return false;
  const x = value as Partial<StateThresholdDefinition>;
  if (
    x.version !== 1 ||
    x.evaluation !== 'manual' ||
    !['tier_ii', 'ca_hmbp', 'nj_crtk', 'or_cr2k'].includes(x.program ?? '') ||
    ![x.sourceKey, x.title, x.citation].every((v) => typeof v === 'string' && v.length > 0) ||
    !Array.isArray(x.conditions) ||
    !x.conditions.every((v) => typeof v === 'string') ||
    !Array.isArray(x.quantities)
  )
    return false;
  const selector = x.selector;
  if (!selector || !['hazard_class', 'material', 'list', 'program_review'].includes(selector.kind)) return false;
  if (
    selector.kind === 'hazard_class' &&
    (!['DOT', 'state'].includes(selector.system) ||
      !Array.isArray(selector.classes) ||
      !selector.classes.every((v) => typeof v === 'string'))
  )
    return false;
  if (selector.kind === 'material' && typeof selector.category !== 'string') return false;
  if (selector.kind === 'list' && (typeof selector.list !== 'string' || !isNullableString(selector.facilityCondition)))
    return false;
  return x.quantities.every(
    (q) =>
      q &&
      (q.kind === 'presence' ||
        q.kind === 'tpq' ||
        (q.kind === 'amount' &&
          Number.isFinite(q.value) &&
          q.value > 0 &&
          ['lb', 'gal', 'ft3'].includes(q.unit) &&
          isNullableString(q.physicalState) &&
          isNullableString(q.conditions))),
  );
}
